import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const API_URL = `${API_BASE}/bookings`;

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeStatus, setActiveStatus] = useState("active");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedBookings(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBookings.size === filteredBookings.length) {
      setSelectedBookings(new Set());
    } else {
      setSelectedBookings(new Set(filteredBookings.map(b => b._id)));
    }
  };

  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState("");
  const bulkMenuRef = useRef(null);

  useEffect(() => {
    if (!bulkMenuOpen) return undefined;
    const onDown = (e) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) setBulkMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [bulkMenuOpen]);

  // Apply a status to every selected booking. Uses the same full-document PUT
  // as the booking detail page, so the server's own rules still apply (e.g. a
  // booking without an executed Owner's Risk Statement can't be confirmed) —
  // anything the server refuses is counted and reported rather than hidden.
  const handleBulkStatus = async (newStatus) => {
    const targets = bookings.filter((b) => selectedBookings.has(b._id));
    const skipped = targets.filter((b) => {
      const cur = String(b.status || "pending").toLowerCase();
      return cur === newStatus || ["cancelled", "completed"].includes(cur);
    });
    const todo = targets.filter((b) => !skipped.includes(b));

    if (todo.length === 0) {
      setBulkResult(`Nothing to update — the selected bookings are already ${newStatus} or closed.`);
      return;
    }

    const label = toTitle(newStatus).toLowerCase();
    const ok = window.confirm(
      `Mark ${todo.length} booking${todo.length > 1 ? "s" : ""} as ${label}?` +
      (skipped.length ? `\n${skipped.length} will be skipped (already ${label} or closed).` : "")
    );
    if (!ok) return;

    setBulkMenuOpen(false);
    setBulkBusy(true);
    setBulkResult("");
    const token = localStorage.getItem("token");
    const failed = [];

    await Promise.all(todo.map(async (b) => {
      try {
        const res = await fetch(`${API_BASE}/bookings/${b._id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          // Status-only body: re-sending the whole booking (incl. its sailing)
          // makes the server re-validate the sailing, which refuses anything
          // on a sailing that is no longer Open/Draft.
          body: JSON.stringify({
            status: newStatus,
            workflowSnapshot: {
              ...(b.workflowSnapshot || {}),
              lastStatus: b.status || "pending",
              currentStatus: newStatus,
              updatedAt: new Date().toISOString(),
            },
          }),
        });
        if (!res.ok) {
          const text = await res.text();
          let msg = text;
          try { const j = JSON.parse(text); msg = j.message || j.error || text; } catch (e) { /* plain text */ }
          throw new Error(msg || `HTTP ${res.status}`);
        }
      } catch (err) {
        failed.push({ ref: b.bookingRef || b._id?.slice(-6).toUpperCase(), reason: err.message });
      }
    }));

    const done = todo.length - failed.length;
    setBulkResult(
      `${done} booking${done === 1 ? "" : "s"} marked ${label}.` +
      (skipped.length ? ` ${skipped.length} skipped.` : "") +
      (failed.length ? ` Could not update — ${failed.slice(0, 3).map((f) => `${f.ref}: ${f.reason}`).join(" | ")}${failed.length > 3 ? ` (+${failed.length - 3} more)` : ""}` : "")
    );
    setSelectedBookings(new Set());
    setBulkBusy(false);
    await fetchBookings();
  };

  const handleDeleteSelected = async () => {
    if (selectedBookings.size === 0) return;
    const confirmed = window.confirm(`Delete ${selectedBookings.size} booking${selectedBookings.size > 1 ? "s" : ""}? This cannot be undone.`);
    if (!confirmed) return;
    try {
      setDeleting(true);
      const token = localStorage.getItem("token");
      await Promise.all([...selectedBookings].map(id =>
        fetch(`${API_BASE}/bookings/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        })
      ));
      setSelectedBookings(new Set());
      await fetchBookings();
    } catch (err) {
      console.error(err);
      alert("Failed to delete some bookings.");
    } finally {
      setDeleting(false);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);

      const response = await fetch(API_URL);
      const data = await response.json();

      setBookings(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const filteredBookings = useMemo(() => {
    return bookings.filter((item) => {
      const ref =
        item.bookingRef ||
        item.ref ||
        item._id?.slice(-6).toUpperCase() ||
        "";

      const customer =
        item.customerName ||
        item.customer ||
        "Unknown Customer";

      const type = String(
        item.type ||
          item.bookingType ||
          "freight"
      ).toLowerCase();

      const status = String(item.status || "pending").toLowerCase();

      const source = String(item.source || "manual").toLowerCase();

      const sailing =
        item.sailingSnapshot?.route ||
        item.sailingSnapshot?.sailingRef ||
        item.sailingName ||
        item.requestedSailing ||
        item.requestedSailingRef ||
        item.route ||
        "";

      const matchesSearch =
        search.trim() === "" ||
        ref.toLowerCase().includes(search.toLowerCase()) ||
        String(customer).toLowerCase().includes(search.toLowerCase()) ||
        String(sailing).toLowerCase().includes(search.toLowerCase());

      const matchesTopStatus =
        activeStatus === "all" ||
        (activeStatus === "active"
          ? !["completed", "cancelled"].includes(status)
          : status === activeStatus);

      const matchesType =
        typeFilter === "all" || type === typeFilter;

      const matchesStatus =
        statusFilter === "all" || status === statusFilter;

      const matchesSource =
        sourceFilter === "all" ||
        (sourceFilter === "online"
          ? ["online", "customer_portal"].includes(source)
          : source === sourceFilter);

      return (
        matchesSearch &&
        matchesTopStatus &&
        matchesType &&
        matchesStatus &&
        matchesSource
      );
    });
  }, [
    bookings,
    search,
    activeStatus,
    typeFilter,
    statusFilter,
    sourceFilter,
  ]);

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>

        <p className="text-sm text-gray-500 mt-1">
          View, search and manage freight and livestock booking requests.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
  "active",
  "all",
  "pending",
  "confirmed",
  "invoiced",
  "completed",
  "cancelled",
].map(
          (status) => (
            <button
              key={status}
              onClick={() => setActiveStatus(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                activeStatus === status
                  ? "bg-black text-white"
                  : "border border-gray-300 bg-white hover:bg-gray-50"
              }`}
            >
              {status === "all" ? "All Bookings" : status === "active" ? "Active" : toTitle(status)}
            </button>
          )
        )}
      </div>

      <div className="border border-gray-300 rounded-xl bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search customer, sailing or booking ref..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          />

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Types</option>
            <option value="livestock">Livestock</option>
            <option value="freight">Freight</option>
            <option value="mixed">Mixed</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="invoiced">Invoiced</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Sources</option>
            <option value="manual">Manual</option>
            <option value="online">Portal</option>
          </select>
        </div>
      </div>

      <div className="border border-gray-300 rounded-xl bg-white">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Booking List</h2>
            <p className="text-sm text-gray-500">
              Showing {filteredBookings.length} of {bookings.length} bookings.
              {selectedBookings.size > 0 && <span className="ml-2 font-semibold text-slate-700">{selectedBookings.size} selected.</span>}
            </p>
          </div>
          {selectedBookings.size > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedBookings(new Set())}
                className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-900"
              >
                Clear
              </button>

              <div className="relative" ref={bulkMenuRef}>
                <button
                  type="button"
                  disabled={bulkBusy || deleting}
                  onClick={() => setBulkMenuOpen((o) => !o)}
                  className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
                >
                  {bulkBusy ? "Updating..." : `Actions (${selectedBookings.size}) ▾`}
                </button>

                {bulkMenuOpen && (
                  <div className="absolute right-0 z-20 mt-2 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                    <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Mark selected as
                    </div>
                    <button type="button" onClick={() => handleBulkStatus("confirmed")}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                      Confirmed
                    </button>
                    <button type="button" onClick={() => handleBulkStatus("completed")}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                      Completed
                    </button>
                    <button type="button" onClick={() => handleBulkStatus("cancelled")}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">
                      Cancelled
                    </button>

                    <div className="my-1 border-t border-slate-100" />
                    <button type="button" onClick={() => { setBulkMenuOpen(false); handleDeleteSelected(); }}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50">
                      Delete selected…
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {bulkResult && (
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-slate-50 px-5 py-3 text-sm text-slate-700">
            <span>{bulkResult}</span>
            <button type="button" onClick={() => setBulkResult("")} className="text-slate-400 hover:text-slate-700">×</button>
          </div>
        )}

        <div className="overflow-x-auto rounded-b-xl">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-300">
              <tr className="text-left">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filteredBookings.length > 0 && selectedBookings.size === filteredBookings.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 font-semibold text-gray-700">Ref</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Date/Time</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Customer</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Requested Sailing</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Source</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 font-semibold text-gray-700 text-right">View</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">Loading bookings...</td>
                </tr>
              ) : filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-gray-500">No bookings match those filters.</td>
                </tr>
              ) : (
                filteredBookings.map((item) => {
                  const ref =
                    item.bookingRef ||
                    item.ref ||
                    item._id?.slice(-6).toUpperCase();

                  const type =
                    item.type ||
                    item.bookingType ||
                    "freight";

                  const sailing =
                    item.sailingSnapshot?.route ||
                    item.sailingSnapshot?.sailingRef ||
                    item.sailingName ||
                    item.requestedSailing ||
                    item.requestedSailingRef ||
                    item.route ||
                    "Unassigned";

                  return (
                    <tr
                      key={item._id || ref}
                      className={`border-t border-gray-200 hover:bg-gray-50 ${selectedBookings.has(item._id) ? "bg-slate-50" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedBookings.has(item._id)}
                          onChange={() => toggleSelect(item._id)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{ref}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleString("en-NZ", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </td>
                      <td className="px-4 py-3">{item.customerName || item.customer || "New Customer"}</td>
                      <td className="px-4 py-3">{toTitle(type)}</td>
                      <td className="px-4 py-3">{sailing}</td>
                      <td className="px-4 py-3"><SourceBadge source={item.source} /></td>
                      <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/dashboard/bookings/${item._id}`}
                          className="border border-gray-300 px-3 py-1 rounded-lg text-xs font-medium hover:bg-gray-100 inline-block"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <Link
          to="/dashboard/bookings/new"
          className="bg-black text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800"
        >
          + New Booking
        </Link>
      </div>
    </div>
  );
};

function toTitle(value) {
  if (!value) return "";

  return String(value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function StatusBadge({ status }) {
  const clean = String(status || "pending").toLowerCase();
  const value = toTitle(clean);

  const styles =
    clean === "confirmed"
      ? "bg-green-100 text-green-700"
      : clean === "invoiced"
      ? "bg-blue-100 text-blue-700"
      : clean === "completed"
      ? "bg-gray-900 text-white"
      : clean === "cancelled"
      ? "bg-red-100 text-red-700"
      : "bg-amber-100 text-amber-700";

  return (
    <span
      className={`inline-block px-2 py-1 rounded text-xs font-medium ${styles}`}
    >
      {value}
    </span>
  );
}

function SourceBadge({ source }) {
  const clean = String(source || "manual").toLowerCase();
  const isPortal = clean === "online" || clean === "customer_portal";
  const value = isPortal ? "Portal" : toTitle(clean);

  const styles = isPortal
    ? "bg-blue-100 text-blue-700"
    : "bg-gray-100 text-gray-700";

  return (
    <span
      className={`inline-block px-2 py-1 rounded text-xs font-medium ${styles}`}
    >
      {value}
    </span>
  );
}

export default Bookings;